import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  EscopoDoRelatorio, PessoaAutorizada, StatusDoCompartilhamento, TextoDoEscopo,
  convidarParaRelatorio, getCompartilhamentos, mudarEscopoDoRelatorio, removerDoRelatorio,
} from '../services/api';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import { useConfirmar } from '../contexts/Confirmacao';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { LIMITES } from '../utils/limites';
import { useBotaoOcupado } from '../utils/botaoOcupado';
import './CompartilharRelatorio.css';

/**
 * Quem recebe o meu relatório mensal.
 *
 * ── O que esta tela precisa deixar óbvio ─────────────────────────────────
 *
 * Que cadastrar um email NÃO manda nada. A pessoa do outro lado decide, e até
 * ela decidir nada sai. Quem não entende isso ou não usa o recurso, ou usa
 * achando que já compartilhou — e as duas leituras erradas custam caro.
 *
 * Por isso o estado de cada linha aparece em palavra, não em cor: "esperando
 * resposta" e "recebendo" dizem coisas diferentes, e um pontinho verde não
 * diria nem uma nem outra.
 */
const ROTULO: Record<StatusDoCompartilhamento, string> = {
  pendente: 'esperando resposta',
  aceito: 'recebendo',
  recusado: 'recusou',
  revogado: 'removida por você',
  saiu: 'pediu para sair',
};

/**
 * A data, sem o dia a menos.
 *
 * `new Date('2026-05-10')` é meia-noite em UTC, e São Paulo está três horas
 * atrás: a tela mostraria 09/05. As rotas devolvem timestamp completo, então
 * em produção isso não aparece — mas a fixture da sessão falsa usa data seca,
 * e foi exatamente ali que o dia errado apareceu na revisão.
 *
 * Data seca é lida como data, e não como instante. É a mesma regra do
 * `brazilDate` no servidor, do lado de cá.
 */
const data = (iso: string | null): string => {
  if (!iso) return '';
  const seca = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = seca
    ? new Date(Number(seca[1]), Number(seca[2]) - 1, Number(seca[3]))
    : new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

/**
 * Uma linha da lista.
 *
 * O botão de remover some para quem recusou ou saiu — ali não há o que
 * remover, e a linha existe justamente para lembrar que essa pessoa disse não.
 * Oferecer o botão e recusar no clique seria pior que não oferecer.
 */
const Linha: React.FC<{
  p: PessoaAutorizada;
  escopos: Record<EscopoDoRelatorio, TextoDoEscopo>;
  ocupado: boolean;
  onEscopo: (id: string, escopo: EscopoDoRelatorio) => void;
  onRemover: (p: PessoaAutorizada) => void;
}> = ({ p, escopos, ocupado, onEscopo, onRemover }) => {
  const disseNao = p.status === 'recusado' || p.status === 'saiu';
  /*
   * A que ela já removeu (13/10): o seletor e o "Remover" continuavam na
   * linha e não faziam nada. Agora a linha diz que a pessoa não recebe mais e
   * oferece apagar da lista. Quem disse não continua sem botão: aquele
   * registro é a memória do "não".
   */
  const removida = p.status === 'revogado';

  return (
    <li className={`cr-linha cr-s-${p.status}`}>
      <div className="cr-linha-topo">
        <div className="cr-quem">
          {p.label && <span className="cr-apelido">{p.label}</span>}
          <span className="cr-email">{p.email}</span>
        </div>
        <span className="cr-status">{ROTULO[p.status]}</span>
      </div>

      <div className="cr-linha-baixo">
        {disseNao ? (
          <span className="cr-nota">
            {p.status === 'recusado'
              ? `Recusou em ${data(p.respondidoEm)}. Não dá para convidar de novo.`
              : `Saiu em ${data(p.encerradoEm)}. Não dá para convidar de novo.`}
          </span>
        ) : removida ? (
          <>
            <span className="cr-nota">
              {p.encerradoEm ? `Removida em ${data(p.encerradoEm)}. ` : ''}Não recebe mais nada. Dá para convidar de novo.
            </span>
            <button className="cr-remover" disabled={ocupado} onClick={() => onRemover(p)}>
              Apagar da lista
            </button>
          </>
        ) : (
          <>
            <select
              className="cr-escopo"
              value={p.escopo}
              disabled={ocupado}
              aria-label={`O que ${p.email} recebe`}
              onChange={e => onEscopo(p.id, e.target.value as EscopoDoRelatorio)}
            >
              <option value="resumo">{escopos.resumo.titulo}</option>
              <option value="completo">{escopos.completo.titulo}</option>
            </select>
            <button className="cr-remover" disabled={ocupado} onClick={() => onRemover(p)}>
              Remover
            </button>
          </>
        )}
      </div>

      {p.status === 'aceito' && p.ultimoEnvioEm && (
        <span className="cr-nota">Último relatório enviado em {data(p.ultimoEnvioEm)}.</span>
      )}
      {p.status === 'aceito' && !p.ultimoEnvioEm && (
        /*
         * A pergunta que essa pessoa faria em seguida, respondida antes.
         * Aceitou hoje, e o relatório é mensal — sem esta linha pareceria que
         * alguma coisa não funcionou.
         */
        <span className="cr-nota">Vai receber no próximo relatório mensal.</span>
      )}
    </li>
  );
};

/*
 * O que veio do servidor, guardado por conta (22/10). Antes o modal abria vazio
 * e o "Recebe… / Não inclui…" aparecia depois, empurrando o botão. Agora abre
 * com o que já veio e confere em silêncio; o menu do avatar já busca antes.
 * A frase do escopo continua saindo só do servidor (`TEXTO_DO_ESCOPO`).
 */
type Compartilhamentos = Awaited<ReturnType<typeof getCompartilhamentos>>;
const guardados = new Map<string, Compartilhamentos>();
const buscando = new Map<string, Promise<Compartilhamentos>>();

export const buscarCompartilhamentos = (userId = ''): Promise<Compartilhamentos> => {
  const emCurso = buscando.get(userId);
  if (emCurso) return emCurso;
  const pedido = getCompartilhamentos()
    .then(r => { guardados.set(userId, r); return r; })
    .finally(() => buscando.delete(userId));
  buscando.set(userId, pedido);
  return pedido;
};

const CompartilharRelatorio: React.FC<{ onClose: () => void; userId?: string }> = ({ onClose, userId = '' }) => {
  const confirmar = useConfirmar();
  useTravarScroll();
  useFechavelComVoltar(true, onClose);
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);

  const jaVeio = guardados.get(userId);
  const [pessoas, setPessoas] = useState<PessoaAutorizada[] | null>(jaVeio?.pessoas ?? null);
  const [escopos, setEscopos] = useState<Record<EscopoDoRelatorio, TextoDoEscopo> | null>(jaVeio?.escopos ?? null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Enviar, trocar o escopo e remover dividem `ocupado`: quem foi tocado se enche.
  useBotaoOcupado(caixa, ocupado);

  const [email, setEmail] = useState('');
  const [apelido, setApelido] = useState('');
  const [escopoNovo, setEscopoNovo] = useState<EscopoDoRelatorio>('resumo');

  const carregar = useCallback(async () => {
    try {
      const r = await buscarCompartilhamentos(userId);
      setPessoas(r.pessoas);
      setEscopos(r.escopos);
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui carregar quem recebe o seu relatório.'));
    }
  }, [userId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const convidar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      await convidarParaRelatorio(email.trim(), escopoNovo, apelido.trim() || undefined);
      setEmail('');
      setApelido('');
      await carregar();
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não consegui enviar o convite.'));
    } finally {
      setOcupado(false);
    }
  };

  const trocarEscopo = async (id: string, escopo: EscopoDoRelatorio) => {
    setOcupado(true);
    setErro(null);
    try {
      await mudarEscopoDoRelatorio(id, escopo);
      await carregar();
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não consegui mudar o que essa pessoa recebe.'));
    } finally {
      setOcupado(false);
    }
  };

  const remover = async (p: PessoaAutorizada) => {
    /*
     * A confirmação existe porque remover quem já ACEITOU é visível do outro
     * lado: a pessoa simplesmente para de receber. Não é um desfazer barato.
     *
     * E é a caixa do PRODUTO, não a do sistema — o `window.confirm` sumiu deste
     * app justamente por aparecer colada no topo do navegador, com o endereço
     * do site em cima, no meio de uma tela que a pessoa reconhece como dela.
     */
    const apagar = p.status === 'revogado';
    const ok = await confirmar({
      titulo: apagar ? 'Apagar da lista?' : p.status === 'aceito' ? 'Parar de compartilhar?' : 'Cancelar o convite?',
      alvo: p.label || p.email,
      texto: apagar
        ? 'Essa pessoa já não recebe nada. A linha some da lista, e você pode convidar de novo quando quiser.'
        : p.status === 'aceito'
          ? 'Essa pessoa para de receber o seu relatório a partir do próximo mês. Você pode convidar de novo depois.'
          : 'O convite deixa de valer. Se essa pessoa clicar no link do email, vai ver que ele foi cancelado.',
      acao: apagar ? 'Apagar' : p.status === 'aceito' ? 'Parar de enviar' : 'Cancelar convite',
      perigo: true,
    });
    if (!ok) return;

    setOcupado(true);
    setErro(null);
    try {
      await removerDoRelatorio(p.id);
      await carregar();
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não consegui remover essa pessoa.'));
    } finally {
      setOcupado(false);
    }
  };

  const corpo = (
    <div className="cr-overlay" onClick={onClose} ref={caixa}>
      <div className="cr-modal" onClick={e => e.stopPropagation()}>
        <div className="cr-banda">
          <h2>Compartilhar relatório</h2>
          <button className="cr-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="cr-corpo">
          <p className="cr-intro">
            Todo mês você recebe um relatório do mês que passou. Aqui você autoriza
            outras pessoas a receberem o mesmo email: um contador, alguém da família,
            quem divide as contas com você.
          </p>
          {/*
            A frase mais importante da tela.
            
            Sem ela, cadastrar um email parece compartilhar. Parece, e não é:
            a pessoa do outro lado ainda precisa aceitar. Quem não entende isso
            fica achando que já dividiu o relatório quando não dividiu nada.
          */}
          <p className="cr-aviso">
            Cadastrar um email <strong>não envia nada</strong>. A pessoa recebe um convite,
            e só passa a receber depois de aceitar.
          </p>

          {erro && <p className="cr-erro">{erro}</p>}

          <form className="cr-form" onSubmit={convidar}>
            <input
              className="cr-campo"
              type="email"
              inputMode="email"
              placeholder="email da pessoa"
              value={email}
              disabled={ocupado}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <input
              className="cr-campo"
              type="text"
              placeholder="como você chama essa pessoa (opcional)"
              maxLength={LIMITES.nome}
              value={apelido}
              disabled={ocupado}
              onChange={e => setApelido(e.target.value)}
            />
            <select
              className="cr-campo"
              value={escopoNovo}
              disabled={ocupado}
              aria-label="O que essa pessoa vai receber"
              onChange={e => setEscopoNovo(e.target.value as EscopoDoRelatorio)}
            >
              <option value="resumo">Resumo do mês</option>
              <option value="completo">Relatório completo</option>
            </select>

            {escopos && (
              <div className="cr-explica">
                <p className="cr-explica-item">✅ Recebe {escopos[escopoNovo].inclui}.</p>
                <p className="cr-explica-item fraco">🚫 Não inclui {escopos[escopoNovo].naoInclui}.</p>
              </div>
            )}

            <button className="cr-enviar" type="submit" disabled={ocupado || !email.trim()}>
              Enviar convite
            </button>
          </form>

          {!pessoas && !erro && <p className="cr-nota">Carregando…</p>}

          {pessoas && pessoas.length === 0 && (
            <p className="cr-nota">Ninguém recebe o seu relatório além de você.</p>
          )}

          {pessoas && pessoas.length > 0 && escopos && (
            <ul className="cr-lista">
              {pessoas.map(p => (
                <Linha key={p.id} p={p} escopos={escopos} ocupado={ocupado}
                       onEscopo={trocarEscopo} onRemover={remover} />
              ))}
            </ul>
          )}

          <p className="cr-rodape">
            Quem recebe pode sair a qualquer momento, por um link no rodapé de todo
            relatório, e você é avisada por email quando isso acontece. Nada mais
            da sua conta é compartilhado: nem o app, nem o histórico, nem os
            lançamentos do dia a dia.
          </p>
        </div>
      </div>
    </div>
  );

  // Portal pelo mesmo motivo do monitor: o menu do avatar cria contexto de
  // empilhamento com `transform`, e o modal ficaria preso dentro dele.
  return createPortal(corpo, document.body);
};

export default CompartilharRelatorio;
